import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Input, Spin } from 'antd';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { PoweroffOutlined, SearchOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { isMobileOnly } from 'react-device-detect';
import _ from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import { userInfoVar } from 'src/apollo/cache';
import LeftMenu2 from 'src/components/LevelUp/LeftMenu/index2';
import LevelUpLibraryMenuGroup3 from 'src/components/common/LevelUpLibraryMenuGroup3';
import { FAVORITE_BOOK } from 'src/operations/mutations/externalUserStudy';
import { EXTERNAL_USER_STUDY_LEVELUP } from 'src/operations/queries/externalStudy';
import { GET_STUDENT_LUPUSER, GET_TEACHER_USER } from 'src/operations/queries/getUser';
import { getLixileScoreObject } from 'src/utils/index';

const RowFlex = styled.div`
  display: flex;
  flex-direction: row;
`;

const Wrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  height: 100vh;
  padding-top: ${({ $isMobile }) => ($isMobile ? '0' : '64px')};
  overflow: ${({ $isMobile }) => ($isMobile ? 'auto' : 'hidden')};
  box-sizing: border-box;
  background:
    radial-gradient(ellipse 800px 500px at 15% 30%, rgba(34, 211, 238, 0.08) 0%, transparent 70%),
    radial-gradient(ellipse 600px 400px at 85% 70%, rgba(16, 185, 129, 0.06) 0%, transparent 70%),
    radial-gradient(ellipse 500px 300px at 50% 10%, rgba(139, 92, 246, 0.04) 0%, transparent 70%),
    #e8f4f8;
`;

const MobileHeader = styled(RowFlex)`
  justify-content: space-between;
  align-items: center;
  height: 64px;
  padding: 12px 16px 14px;
  box-sizing: border-box;
  background: linear-gradient(135deg, #0c1a2e 0%, #1e3a5f 60%, #0891b2 100%);
  position: sticky;
  top: 0;
  z-index: 30;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
    background-size: 18px 18px;
    pointer-events: none;
  }
`;

const MobileHeaderBrand = styled(RowFlex)`
  position: relative;
  z-index: 1;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const MobileHeaderLogo = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 14px;
  background: linear-gradient(135deg, #0891b2, #06b6d4);
  background-image: url('/images/LevelUp/levelup_owl.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: 24px 24px;
  box-shadow: 0 4px 12px rgba(8, 145, 178, 0.4);
  flex-shrink: 0;
`;

const MobileHeaderName = styled.div`
  font-family: 'Baloo 2', sans-serif;
  font-size: 18px;
  font-weight: 900;
  color: #ffffff;
  line-height: 1;
  span {
    color: #22d3ee;
  }
`;

const MobileHeaderActions = styled(RowFlex)`
  position: relative;
  z-index: 1;
  gap: 8px;
  align-items: center;
`;

const MobileIconButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1.5px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const MainWrapper = styled.div`
  display: flex;
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'column' : 'row')};
  min-height: ${({ $isMobile }) => ($isMobile ? 'auto' : 'calc(100vh - 64px)')};
  height: ${({ $isMobile }) => ($isMobile ? 'auto' : 'calc(100vh - 64px)')};
  width: 100%;
  overflow: ${({ $isMobile }) => ($isMobile ? 'visible' : 'hidden')};
`;

const Main = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: ${({ $isMobile }) => ($isMobile ? 'auto' : '100%')};
  padding: ${({ $isMobile }) => ($isMobile ? '0 12px 16px' : '12px 24px 20px')};
  display: flex;
  flex-direction: column;
  gap: ${({ $isMobile }) => ($isMobile ? '12px' : '16px')};
  overflow: ${({ $isMobile }) => ($isMobile ? 'visible' : 'hidden')};
  box-sizing: border-box;
`;

const MobileTopTabsShell = styled.div`
  position: sticky;
  top: 62px;
  z-index: 18;
  background: #ffffff;
  margin: 0 -12px;
  padding: 0 12px 10px;
  box-shadow: 0 8px 20px rgba(8, 145, 178, 0.08);
`;

const MobileTopTabs = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  padding: 10px 2px 0;
`;

const MobileTopTab = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 56px;
  padding: 4px;
  border-radius: 16px;
  border: 1.5px solid ${(props) => (props.$active ? 'rgba(8,145,178,.22)' : 'transparent')};
  background: ${(props) => (props.$active ? '#eaf7fb' : 'transparent')};
  color: ${(props) => (props.$active ? '#0891b2' : '#4a6380')};
  font-size: 9px;
  font-weight: 800;
  cursor: pointer;
  line-height: 1.1;
  font-family: 'Nunito', sans-serif;
`;

const MainPanel = styled.div`
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px 20px 14px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.78);
  border: 1.5px solid rgba(8, 145, 178, 0.1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.65);
  overflow: hidden;
  @media (max-width: 768px) {
    flex: none;
    min-height: auto;
    padding: 12px 0 0;
    border: none;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    overflow: visible;
  }
`;

const PanelTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;
`;

const PanelCount = styled.div`
  font-family: 'Baloo 2', 'Nunito', sans-serif;
  font-size: 15px;
  font-weight: 800;
  color: #172554;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CountBadge = styled.span`
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  background: rgba(8, 145, 178, 0.12);
  color: #0891b2;
  border: 1px solid rgba(8, 145, 178, 0.18);
  font-family: 'JetBrains Mono', monospace;
`;

const SearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SearchInput = styled(Input)`
  width: 150px;
  height: 36px;
  border-radius: 18px;
  border: 1.5px solid rgba(8, 145, 178, 0.14);
  background: #ffffff;
  font-family: 'Nunito', sans-serif;
  &:hover,
  &.ant-input-affix-wrapper-focused {
    border-color: rgba(8, 145, 178, 0.22);
    box-shadow: none;
  }
`;

const BooksScroll = styled.div`
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(8, 145, 178, 0.06);
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(8, 145, 178, 0.62), rgba(6, 182, 212, 0.52));
    border-radius: 999px;
  }
  @media (max-width: 768px) {
    flex: none;
    min-height: auto;
    overflow: visible;
    padding-right: 0;
  }
`;

const BookGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
  padding-bottom: 8px;
  @media (max-width: 1500px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
`;

const BookCard = styled.button`
  border: 1.5px solid rgba(8, 145, 178, 0.12);
  background: #ffffff;
  border-radius: 24px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.22s ease;
  box-shadow: 0 4px 18px rgba(8, 145, 178, 0.12);
  padding: 0;
  text-align: left;
  &:hover {
    transform: translateY(-6px) scale(1.02);
    border-color: #0891b2;
    box-shadow: 0 10px 28px rgba(8, 145, 178, 0.2);
  }
`;

const BookThumb = styled.div`
  width: 100%;
  aspect-ratio: 3 / 4;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #dff5fb 0%, #c9ebf5 100%);
`;

const BookImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const BookShade = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, transparent 40%, rgba(0, 0, 0, 0.65) 100%);
`;

const BookProgress = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  min-width: 78px;
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  border: 2px solid rgba(255, 212, 164, 0.85);
  background: linear-gradient(135deg, #f6b83f 0%, #f0882b 100%);
  color: #ffffff;
  font-family: 'Baloo 2', 'Nunito', sans-serif;
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 18px rgba(240, 136, 43, 0.28);
  z-index: 2;
`;

const BookLevel = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 3px 8px;
  border-radius: 20px;
  background: rgba(8, 145, 178, 0.88);
  color: #ffffff;
  backdrop-filter: blur(4px);
`;

const BookFavorite = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${(props) => (props.$active ? '#fee2e2' : 'rgba(255,255,255,.92)')};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.18s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 2;
  &:hover {
    transform: scale(1.15);
  }
`;

const BookBody = styled.div`
  padding: 12px 14px 14px;
`;

const BookTitle = styled.div`
  font-size: 11.5px;
  font-weight: 800;
  color: #172554;
  text-align: center;
  line-height: 1.35;
  min-height: 32px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-family: 'Nunito', sans-serif;
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-shrink: 0;
  padding: 4px 0 0;
`;

const PagerButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1.5px solid rgba(8, 145, 178, 0.14);
  background: #ffffff;
  color: #0891b2;
  font-size: 16px;
  font-family: 'Nunito', sans-serif;
  font-weight: 800;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s ease;
  box-shadow: 0 4px 12px rgba(8, 145, 178, 0.1);
  &:hover:not(:disabled) {
    background: #0891b2;
    color: #ffffff;
    border-color: #0891b2;
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const PagerDots = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`;

const PagerDot = styled.button`
  height: 5px;
  width: ${(props) => (props.$active ? '18px' : '5px')};
  border-radius: 3px;
  background: ${(props) => (props.$active ? '#0891b2' : 'rgba(8, 145, 178, 0.18)')};
  border: none;
  cursor: pointer;
  transition: all 0.25s ease;
  padding: 0;
`;

const EmptyBox = styled.div`
  flex: 1;
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 24px;
  border: 1.5px dashed rgba(8, 145, 178, 0.18);
  color: #6b7280;
  background: rgba(255, 255, 255, 0.72);
  font-family: 'Baloo 2', 'Nunito', sans-serif;
  font-size: 24px;
  font-weight: 700;
`;

const MobileBottomNav = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  background: #ffffff;
  border-top: 1.5px solid rgba(8, 145, 178, 0.12);
  box-shadow: 0 -4px 20px rgba(8, 145, 178, 0.1);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  z-index: 40;
`;

const MobileBottomNavItem = styled.button`
  padding: 10px 4px 8px;
  border: 0;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  color: ${(props) => (props.$active ? '#0891b2' : '#8fa3bb')};
  font-size: 10px;
  font-weight: 700;
`;

const PAGE_SIZE = 10;

export default function InProgressBookPage() {
  const { student_idx } = queryString.parse(window.location.search);
  const history = useHistory();
  const isMobileView = window.isNativeAppMobile || isMobileOnly;
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(0);
  const [currentLevel, setCurrentLevel] = useState(null);
  const user_idx = userInfoVar()?.idx || null;

  const setLexileValue = (lexile = '') => {
    if (lexile.includes('BR')) return 0;
    if (lexile.includes('NP')) return 1000;
    return parseInt(lexile, 10);
  };

  const setMxLevel = (lexile) => getLixileScoreObject(lexile, 'A-1');

  const getLatestClassLevel = (classes = []) => {
    const latestClass = _.orderBy(
      classes.filter((classInfo) => classInfo?.levelup_level),
      [
        (classInfo) => {
          const beginDate = moment(classInfo?.begin_date);
          return beginDate.isValid() ? beginDate.valueOf() : 0;
        },
      ],
      ['desc'],
    )[0];

    return latestClass?.levelup_level || '';
  };

  let getUserType = GET_STUDENT_LUPUSER;
  if (userInfoVar() && userInfoVar()?.type !== '0' && userInfoVar()?.type !== 0) {
    getUserType = GET_TEACHER_USER;
  }

  useQuery(getUserType, {
    variables: {},
    onCompleted: (data) => {
      if (!data?.getUserData) return;
      let tempData = data.getUserData;
      if (tempData.class_teacher) {
        tempData = Object.assign({}, tempData, { classes: tempData.class_teacher.map((item) => item.class) });
      } else {
        tempData = Object.assign({}, tempData, { classes: tempData.class_student.map((item) => item.class) });
      }

      if (tempData?.user_levelup_tests?.length > 0) {
        const sortedTests = _.orderBy(
          tempData.user_levelup_tests.filter((item) => item.level),
          ['test_end_date'],
          ['desc'],
        );
        const latestTest = sortedTests[0];
        if (latestTest?.lexile && latestTest?.level) {
          setCurrentLevel(latestTest.level);
          return;
        }
      }

      setCurrentLevel(getLatestClassLevel(tempData?.classes));
    },
  });

  const { data, loading, refetch } = useQuery(EXTERNAL_USER_STUDY_LEVELUP, {
    variables: {
      user_idx: student_idx ? parseInt(student_idx, 10) : parseInt(user_idx, 10),
      type: 'levelup',
      status: 1,
    },
    fetchPolicy: 'no-cache',
    skip: !user_idx,
  });

  const [favBook] = useMutation(FAVORITE_BOOK, {
    onCompleted: () => {
      refetch();
    },
  });

  const studyData = useMemo(() => {
    if (!data?.getExternalStuiesList?.length) return [];
    return data.getExternalStuiesList
      .sort((a, b) => (moment(a.study_date).isSameOrAfter(b.study_date) ? -1 : 1))
      .map((item, key) => {
        const {
          study_date,
          article_data,
          data: rawData,
          last_study_timestamp,
          assigned_idx,
          is_fav,
        } = item;

        const {
          lexile_val,
          title,
          mx_level: level,
          pages,
          code,
          book_no,
          is_voca,
          is_interactive,
          genre__1,
        } = JSON.parse(
          article_data || {
            title: '',
            level: '',
          },
        );

        const parsedData = JSON.parse(rawData) || {
          gamePlayResults: [],
        };

        const lexileVal = !_.isNil(lexile_val) ? lexile_val : setLexileValue('');
        const mxObj = setMxLevel(lexileVal);
        const achievement = parsedData?.gamePlayResults?.length
          ? parsedData.gamePlayResults.reduce(
              (acc, cur) => {
                if (cur.numberOfAttempts) {
                  acc.score += cur.numberOfAttempts;
                  acc.total += cur.numberOfElements;
                } else if (cur.numberOfCorrectAttempts) {
                  acc.score += cur.numberOfCorrectAttempts;
                  acc.total += cur.numberOfElements;
                } else if (cur.status === 1) {
                  acc.score += 1;
                  acc.total += 1;
                }
                return acc;
              },
              { score: 0, total: 0 },
            )
          : { score: 0, total: 0 };

        const progress = achievement.total > 0 ? Math.max(1, Math.round((achievement.score * 100) / achievement.total)) : 20;

        return {
          key: `levelup-progress-${key}`,
          title,
          level: level || mxObj?.mx,
          mx_level: level,
          lexile_val,
          lexileVal,
          pages,
          grl: genre__1 || '',
          image: `https://cdn.cloubot.com/LEVELUP/covers/${code}.png`,
          code,
          book_no,
          is_voca,
          is_interactive,
          assigned_idx,
          selfStudy: assigned_idx === null,
          last_study_timestamp: last_study_timestamp ? `${last_study_timestamp.slice(0, 10)} ${last_study_timestamp.slice(11, 19)}` : '-',
          articleData: {
            lexile: lexile_val,
            issue_date: moment(study_date).format('YYYY-MM-DD HH:mm:ss'),
            image: `https://cdn.cloubot.com/LEVELUP/covers/${code}.png`,
            level,
            title,
            assigned_idx,
            book_no,
            code,
            is_voca,
            is_interactive,
          },
          book_id: item?.external_study_idx || parsedData?.bookId,
          idx: item?.idx,
          is_fav,
          progress,
        };
      });
  }, [data]);

  const books = useMemo(() => {
    if (!searchValue.trim()) return studyData;
    return studyData.filter((item) => (item?.title || '').toLowerCase().includes(searchValue.toLowerCase()));
  }, [studyData, searchValue]);

  const totalPages = Math.max(1, Math.ceil(books.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);

  const pagedBooks = useMemo(() => books.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [books, page]);

  const formatMxLevel = (level) => {
    if (_.isNil(level) || level === '') return '-';
    if (`${level}`.toUpperCase() === 'PH') return 'PH';
    const numericLevel = parseInt(level, 10);
    if (Number.isNaN(numericLevel)) return `${level}`;
    return String(numericLevel).padStart(2, '0');
  };

  const formatLexile = (lexileValue) => {
    if (_.isNil(lexileValue) || lexileValue === '') return '-';
    const numericValue = parseInt(lexileValue, 10);
    if (Number.isNaN(numericValue)) return `${lexileValue}`;
    return numericValue < 0 ? `BR${Math.abs(numericValue)}L` : `${numericValue}L`;
  };

  const getBookPath = (book) => {
    if (userInfoVar()?.type > 0 && userInfoVar()?.company_name !== 'gnb') {
      return `/level-up/description/${book?.code}`;
    }
    return `/level-up/${book?.is_voca ? 'study' : `${book?.is_interactive ? 'epub-' : ''}reader`}/${book?.code}`;
  };

  const handleBookClick = (book) => {
    const bookState =
      !book?.selfStudy && book?.assigned_idx
        ? { bookData: { ...book, assignedIdx: book?.assigned_idx }, assigned_idx: book?.assigned_idx }
        : { bookData: book, assigned_idx: null };

    history.push({
      pathname: getBookPath(book),
      state: bookState,
    });
  };

  const handleToggleFavorite = async (event, book) => {
    event.preventDefault();
    event.stopPropagation();
    await favBook({
      variables: {
        book_id: book?.book_no,
        type: 'levelup',
      },
    });
  };

  return (
    <Wrapper $isMobile={isMobileView}>
      {isMobileView ? (
        <>
          <MobileHeader>
            <MobileHeaderBrand>
              <MobileHeaderLogo />
              <MobileHeaderName>
                e-<span>Library</span>
              </MobileHeaderName>
            </MobileHeaderBrand>
            <MobileHeaderActions>
              <MobileIconButton type="button" onClick={() => history.push('/level-up/calendar')}>
                {'\u{1F514}'}
              </MobileIconButton>
              <MobileIconButton
                type="button"
                onClick={() => {
                  history.push('/');
                }}
              >
                <PoweroffOutlined />
              </MobileIconButton>
            </MobileHeaderActions>
          </MobileHeader>
        </>
      ) : null}
      <MainWrapper $isMobile={isMobileView}>
        {!isMobileView && (
          <LeftMenu2
            myLevel={parseInt(userInfoVar()?.type || 0, 10) !== 0 ? -2 : currentLevel === 'PH' ? 0 : parseInt(currentLevel, 10)}
            isStudent={parseInt(userInfoVar()?.type || 0, 10) === 0}
          />
        )}
        <Main
          $isMobile={isMobileView}
          style={isMobileView ? { paddingBottom: 'calc(104px + env(safe-area-inset-bottom, 0px))' } : undefined}
        >
          {!isMobileView && <LevelUpLibraryMenuGroup3 currentMenu="in_progress" student_idx={student_idx} />}
          {isMobileView && (
            <MobileTopTabsShell>
              <MobileTopTabs>
                <MobileTopTab type="button" onClick={() => history.push('/level-up/favorite')}>
                  <span>{'\u2764\uFE0F'}</span>
                  <span>Favorite</span>
                </MobileTopTab>
                <MobileTopTab $active type="button">
                  <span>{'\u{1F4D6}'}</span>
                  <span>In Progress</span>
                </MobileTopTab>
                <MobileTopTab type="button" onClick={() => history.push('/level-up/portfolio')}>
                  <span>{'\u2705'}</span>
                  <span>Portfolio</span>
                </MobileTopTab>
                <MobileTopTab type="button" onClick={() => history.push('/level-up/calendar')}>
                  <span>{'\u{1F3C6}'}</span>
                  <span>Pts & Cal</span>
                </MobileTopTab>
              </MobileTopTabs>
            </MobileTopTabsShell>
          )}
          <MainPanel>
            <PanelTop>
              <PanelCount>
                <span>{isMobileView ? 'In Progress' : '총'}</span>
                <CountBadge>{`${books.length}권`}</CountBadge>
              </PanelCount>
              <SearchWrap>
                <SearchInput
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  allowClear
                  bordered={false}
                  placeholder="Search"
                  suffix={<SearchOutlined style={{ color: '#64748b' }} />}
                />
              </SearchWrap>
            </PanelTop>
            {loading ? (
              <EmptyBox>
                <Spin />
              </EmptyBox>
            ) : books.length ? (
              <>
                <BooksScroll>
                  {isMobileView ? (
                    <BookGrid>
                      {books.map((book) => (
                        <BookCard key={`${book?.book_no}-${book?.code}`} type="button" onClick={() => handleBookClick(book)}>
                          <BookThumb>
                            <BookImage src={`https://cdn.cloubot.com/LEVELUP/covers/${book?.code}.png`} alt={book?.title} />
                            <BookShade />
                            <BookProgress>{`${book?.progress || 0}%`}</BookProgress>
                            <BookFavorite type="button" $active={!!book?.is_fav} onClick={(event) => handleToggleFavorite(event, book)}>
                              {book?.is_fav ? '❤️' : '🤍'}
                            </BookFavorite>
                            <BookLevel>{`mx ${formatMxLevel(book?.level)}(${formatLexile(book?.lexileVal)})`}</BookLevel>
                          </BookThumb>
                          <BookBody>
                            <BookTitle>{book?.title}</BookTitle>
                          </BookBody>
                        </BookCard>
                      ))}
                    </BookGrid>
                  ) : (
                    <BookGrid>
                      {pagedBooks.map((book) => (
                        <BookCard key={`${book?.book_no}-${book?.code}`} type="button" onClick={() => handleBookClick(book)}>
                          <BookThumb>
                            <BookImage src={`https://cdn.cloubot.com/LEVELUP/covers/${book?.code}.png`} alt={book?.title} />
                            <BookShade />
                            <BookProgress>{`${book?.progress || 0}%`}</BookProgress>
                            <BookFavorite type="button" $active={!!book?.is_fav} onClick={(event) => handleToggleFavorite(event, book)}>
                              {book?.is_fav ? '❤️' : '🤍'}
                            </BookFavorite>
                            <BookLevel>{`mx ${formatMxLevel(book?.level)}(${formatLexile(book?.lexileVal)})`}</BookLevel>
                          </BookThumb>
                          <BookBody>
                            <BookTitle>{book?.title}</BookTitle>
                          </BookBody>
                        </BookCard>
                      ))}
                    </BookGrid>
                  )}
                </BooksScroll>
                {!isMobileView && totalPages > 1 && (
                  <Pager>
                    <PagerButton type="button" disabled={page === 0} onClick={() => setPage((prev) => Math.max(0, prev - 1))}>
                      {'\u2039'}
                    </PagerButton>
                    <PagerDots>
                      {Array.from({ length: totalPages }).map((_, index) => (
                        <PagerDot key={index} type="button" $active={index === page} onClick={() => setPage(index)} />
                      ))}
                    </PagerDots>
                    <PagerButton type="button" disabled={page >= totalPages - 1} onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}>
                      {'\u203A'}
                    </PagerButton>
                  </Pager>
                )}
              </>
            ) : (
              <EmptyBox>No Studying Books!</EmptyBox>
            )}
          </MainPanel>
        </Main>
      </MainWrapper>
      {isMobileView ? (
        <MobileBottomNav>
          <MobileBottomNavItem type="button" onClick={() => history.push('/level-up?mobilePage=home')}>
            <span>{'\u{1F3E0}'}</span>
            <span>Home</span>
          </MobileBottomNavItem>
          <MobileBottomNavItem type="button" onClick={() => history.push('/level-up/library?lv=1')} $active>
            <span>{'\u{1F4DA}'}</span>
            <span>Library</span>
          </MobileBottomNavItem>
          <MobileBottomNavItem type="button" onClick={() => history.push('/level-up/portfolio')}>
            <span>{'\u{1F4C1}'}</span>
            <span>Portfolio</span>
          </MobileBottomNavItem>
          <MobileBottomNavItem type="button" onClick={() => history.push('/level-up?mobilePage=ranking')}>
            <span>{'\u{1F3C6}'}</span>
            <span>Ranking</span>
          </MobileBottomNavItem>
          <MobileBottomNavItem type="button" onClick={() => history.push('/level-up?mobilePage=profile')}>
            <span>{'\u{1F464}'}</span>
            <span>???뺣낫</span>
          </MobileBottomNavItem>
        </MobileBottomNav>
      ) : null}
    </Wrapper>
  );
}


